import {decorate, observable} from 'mobx';
import {faLayerGroup} from '@fortawesome/free-solid-svg-icons';
import {BaseModel} from './base';
import i18n from '../i18n/i18n';
import {transformation} from '../utils/transformations';
import {dateFormat} from '../config/moment';
import {displayableProperty} from './displayableProperty';
import {displayablePropertyParam} from './displayablePropertyParam';
import {Material} from './material';
import {sortAlphabetically, sortChronologically} from '../components/shared/tables/sorters';
import {CustomPropertyTypes} from './customProperty';

/*
 * Translations:
 * t('batch.model.status.FREE')
 * t('batch.model.status.LOCKED')
 */
export const BatchStatus = Object.freeze({
  FREE: 'FREE',
  LOCKED: 'LOCKED',
});

export class Batch extends BaseModel {
  static faIcon = faLayerGroup;

  searchableProperties = ['no', 'material.no', 'material.name', 'remark'];

  displayableProperties = [
    displayableProperty({
      key: 'no',
      title: i18n.t('batch.model.attributes.no'),
      params: [displayablePropertyParam({path: 'no'})],
      sorter: (a, b) => sortAlphabetically(a.no, b.no),
    }),
    displayableProperty({
      key: 'expireDate',
      title: i18n.t('batch.model.attributes.expireDate'),
      params: [displayablePropertyParam({
        path: 'expireDate', transform: transformation.datetime({format: dateFormat}),
      })],
      sorter: (a, b) => sortChronologically(a.expireDate, b.expireDate),
    }),
    displayableProperty({
      title: i18n.t('batch.model.attributes.createdBy'),
      params: [displayablePropertyParam({path: 'user.fullName', transform: transformation.none})],
      template: '{value}',
      key: 'createdBy',
      sorter: (a, b) => sortAlphabetically(a.no, b.no),
    }),
    displayableProperty({
      title: i18n.t('batch.model.attributes.remark'),
      params: [displayablePropertyParam({path: 'remark'})],
      key: 'remark',
      sorter: (a, b) => sortAlphabetically(a.remark, b.remark),
    }),
  ];

  populateAttributesFromStore(rootStore) {
    super.populateAttributesFromStore(rootStore);
    this.material = rootStore.materialStore.getById(this.materialId);
    this.unitOfMeasure = rootStore.unitOfMeasurementStore.getById(this.unitOfMeasureId);
    this.user = rootStore.userStore.getById(this.createdBy);
  }

  static allDisplayableProperties(rootStore, pathPrefix = '', titlePrefix = '') {
    const allDisplayableProperties = super.allDisplayableProperties(rootStore, pathPrefix, titlePrefix);

    allDisplayableProperties.push(
      ...Material.allDisplayableProperties(rootStore, `${pathPrefix}material.`, titlePrefix),
      ...this.displayableCustomProperties(rootStore, CustomPropertyTypes.Batch, pathPrefix, titlePrefix)
    );

    return allDisplayableProperties;
  }
}

decorate(Batch, {
  id: observable,
  no: observable,
  quantity: observable,
  status: observable,
  materialId: observable,
  material: observable,
  unitOfMeasureId: observable,
  unitOfMeasure: observable,
  remark: observable,
  createdAt: observable,
  createdBy: observable,
  updatedAt: observable,
  updatedBy: observable,
  deletedAt: observable,
  deletedBy: observable,
  expireDate: observable,
  properties: observable,
});
